import { catchError, defer, of } from 'rxjs'
import { shareLatest } from '@/utils/rx-util'
import {
  type PayoutsFeatureOptions,
  type ProfilePayoutMethodViewModelFactory,
} from '@/features/payouts'
import { AdminPayoutHistoryStore } from '@/features/payouts/admin/AdminPayoutHistoryStore'
import { AdminPayoutMethodStore } from '@/features/payouts/admin/AdminPayoutMethodStore'
import { PayoutReviewStore } from '@/features/payouts/admin/PayoutReviewStore'
import { PayoutsAdminViewModel } from '@/features/payouts/admin/PayoutsAdminViewModel'
import { PayoutsAdminApi } from '@/features/payouts/api/admin-api'
import { PayoutsApi } from '@/features/payouts/api/pro-api'
import { PayoutMethodStore } from '@/features/payouts/pro/PayoutMethodStore'
import { ProPayoutStore } from '@/features/payouts/pro/ProPayoutStore'
import { ProPayoutsViewModel } from '@/features/payouts/pro/ProPayoutsViewModel'
import { ProfilePayoutMethodViewModel } from '@/features/payouts/pro/ProfilePayoutMethodViewModel'
import { env } from '@/infra/env'
import { createStreamDownloader } from '@/features/payouts/stream-downloader'

export function InternalPayoutsFeature(options: PayoutsFeatureOptions) {
  const isEnabled = Boolean(env.BIGMONEY_API)
  const downloader = createStreamDownloader()
  const config = { baseURL: env.BIGMONEY_API ?? '/', token$: options.token$ }

  const adminApi = isEnabled ? new PayoutsAdminApi(config) : null

  const api = isEnabled ? new PayoutsApi(config) : undefined

  const payoutReviewStore = adminApi
    ? new PayoutReviewStore(
        adminApi,
        options.primeUser,
        options.primeWorkspace,
        options.getCurrentUser,
        options.listWorkspaces,
        options.searchMembers,
      )
    : null

  const adminPayoutHistoryStore = adminApi
    ? new AdminPayoutHistoryStore(
        adminApi,
        options.primeUser,
        options.primeWorkspace,
      )
    : null

  const proPayoutStore = api
    ? new ProPayoutStore(
        api,
        options.primeWorkspace,
        options.primeJob,
        options.fetchJob,
        options.getCurrentUser,
        options.fetchUserData,
      )
    : null

  const payoutMethodStore = new PayoutMethodStore(options.currentUser$, api)

  const adminPayoutMethodStore = new AdminPayoutMethodStore(
    options.getCurrentUser,
    api,
  )

  const payoutsAdminViewModel =
    payoutReviewStore && adminPayoutHistoryStore
      ? new PayoutsAdminViewModel(
          payoutReviewStore,
          adminPayoutHistoryStore,
          options.newAlert,
          options.createAlertErrorHandler,
          options.createAlertRetryWrapper,
          options.getCurrentUser,
          downloader,
        )
      : null

  const proPayoutsViewModel = proPayoutStore
    ? new ProPayoutsViewModel(
        proPayoutStore,
        options.newAlert,
        options.createAlertErrorHandler,
        options.createAlertRetryWrapper,
        downloader,
      )
    : null

  // A failed check hides the payouts page, like a non-pro account.
  const canViewProPayouts$ = defer(async () =>
    proPayoutStore ? proPayoutStore.checkCurrentUserIsPro() : false,
  ).pipe(
    catchError((err: unknown) => {
      console.error('Failed to check whether the user can view payouts:', err)
      return of(false)
    }),
    shareLatest(),
  )

  const createProfilePayoutMethodViewModel: ProfilePayoutMethodViewModelFactory =
    (member) =>
      new ProfilePayoutMethodViewModel(
        member,
        payoutMethodStore,
        options.redirectToStripeConnect,
      )

  return {
    // Internal
    createProfilePayoutMethodViewModel,
    payoutMethodStore,
    adminPayoutMethodStore,
    payoutsAdminViewModel,
    proPayoutsViewModel,
    canViewProPayouts$,
  }
}
